import { Button, ButtonMode, Modal, ModalWidth, Tooltip } from '@creditsights/styleguide-web';
import { isEqual } from 'lodash';
import { useEffect, useState, useMemo, ReactElement } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useGlobalModalContext } from '../../../../../components/GlobalModal';
import useModalErrorHandler from '../../../../../hooks/modal/useModalErrorHandler';
import {
  useFetchMyContentPreferences,
  useFilterFieldOptions,
  useFilterActiveAnalysts,
} from '../../../../../hooks/myContentPreferences';
import { Preference, NotificationMode } from '../../../../../models/Preference';
import AddPreferencesFormContainer from './AddPreferencesForm/AddPreferencesFormContainer';
import {
  DUPLICATE_PREFERENCES_ERROR_MESSAGE,
  FORM_CATEGORY_VALUES,
  FORM_FIELD_NAMES,
  FORM_HEADER_TITLES,
  SAVE_BUTTON_TOOLTIPS,
} from './AddPreferencesModal.constants';
import { isFormSaveBtnDisabled, isPreferencesUnique, mapFormValuesToPreference } from './AddPreferencesModal.logic';
import { AddPreferencesFormModel } from './AddPreferencesModal.types';
import { GENERIC_ERROR_TEXT } from '../../../../../Base.constants';

interface AddPreferenceModalProps {
  onConfirm: Function;
  selectedPreference?: Preference;
  selectedPreferenceIndex?: number;
}

const AddPreferencesModal = ({
  selectedPreference,
  onConfirm,
  selectedPreferenceIndex,
}: AddPreferenceModalProps): ReactElement => {
  const { closeModal, setError, store } = useGlobalModalContext();
  const { data: allPreferences } = useFetchMyContentPreferences();
  const [selectedSectorId, setSelectedSectorId] = useState('');
  const formError = store.props?.error;
  // react form hook provides isDirty property, but it's being updated only on input blur.
  const { options, subsectorOptionsBySector, isLoading } = useFilterFieldOptions();
  const filteredAnalysts = useFilterActiveAnalysts(selectedPreference?.tags[0]);
  const [isSaving, setIsSaving] = useState(false);
  const isModalCloseable = !isSaving;
  useModalErrorHandler(setIsSaving);

  const addPreferenceModalOptions = useMemo(() => {
    if (filteredAnalysts && options) {
      return { ...options, analyst: filteredAnalysts };
    }
    return options;
  }, [filteredAnalysts, options]);

  const getPreselectedFilterValue = (filterType: keyof typeof FORM_FIELD_NAMES, tagsIds?: string[]) => {
    if (!tagsIds) {
      return '';
    }
    return addPreferenceModalOptions[filterType].find((option) => tagsIds?.includes(String(option.value)))?.value ?? '';
  };

  const getPreselectedSectorValue = () => {
    const preselectedSector = getPreselectedFilterValue(FORM_FIELD_NAMES.sector, selectedPreference?.tags);
    if (preselectedSector && !selectedSectorId) {
      setSelectedSectorId(preselectedSector);
    }
    return preselectedSector ?? '';
  };

  const getPreselectedSubsectorValue = () => {
    const preselectedSector = getPreselectedFilterValue(FORM_FIELD_NAMES.sector, selectedPreference?.tags);
    const preselectedSubsector = subsectorOptionsBySector[preselectedSector]?.find((option) =>
      selectedPreference?.tags.includes(String(option.value))
    )?.value;
    return preselectedSubsector ? String(preselectedSubsector) : '';
  };

  const getPreselectedCategoryValue = () => {
    if (selectedPreference?.tags[0].includes('analyst')) {
      return FORM_CATEGORY_VALUES.Analysts.value;
    }
    if (selectedPreference?.tags[0].includes('type-tag')) {
      return FORM_CATEGORY_VALUES.Reports.value;
    }
    return FORM_CATEGORY_VALUES.Interests.value;
  };

  const form = useForm<AddPreferencesFormModel>({
    defaultValues: {
      [FORM_FIELD_NAMES.category]: getPreselectedCategoryValue(),
      [FORM_FIELD_NAMES.region]: getPreselectedFilterValue(FORM_FIELD_NAMES.region, selectedPreference?.tags),
      [FORM_FIELD_NAMES.creditGrade]: getPreselectedFilterValue(FORM_FIELD_NAMES.creditGrade, selectedPreference?.tags),
      [FORM_FIELD_NAMES.topic]: getPreselectedFilterValue(FORM_FIELD_NAMES.topic, selectedPreference?.tags),
      [FORM_FIELD_NAMES.sector]: getPreselectedSectorValue(),
      [FORM_FIELD_NAMES.subsector]: getPreselectedSubsectorValue(),
      mode: selectedPreference?.mode || NotificationMode.Digest,
      myCS: selectedPreference?.myCS !== false,
      [FORM_FIELD_NAMES.analyst]: getPreselectedFilterValue(FORM_FIELD_NAMES.analyst, selectedPreference?.tags),
      [FORM_FIELD_NAMES.report]: getPreselectedFilterValue(FORM_FIELD_NAMES.report, selectedPreference?.tags),
      [FORM_FIELD_NAMES.assetClass]: getPreselectedFilterValue(FORM_FIELD_NAMES.assetClass, selectedPreference?.tags),
    },
  });
  const formValues = form.watch();
  const saveDisabled = isFormSaveBtnDisabled(isLoading, isSaving, formValues, formError);
  // react form hook provides isDirty property, but it's being updated only on input blur.
  const isDirty = formValues && !isEqual(selectedPreference?.tags ?? [], mapFormValuesToPreference(formValues).tags);

  useEffect(() => {
    if (formValues) {
      const isFormValuesUnique = isPreferencesUnique(
        mapFormValuesToPreference(formValues),
        selectedPreferenceIndex,
        allPreferences
      );
      if (!isFormValuesUnique && (!formError || formError === GENERIC_ERROR_TEXT)) {
        setError(DUPLICATE_PREFERENCES_ERROR_MESSAGE);
      } else if (isFormValuesUnique && formError && formError !== GENERIC_ERROR_TEXT) {
        setError('');
      }
    }
  }, [formValues, allPreferences, selectedPreferenceIndex, formError, setError]);

  const submitForm = (form: AddPreferencesFormModel) => {
    setIsSaving(true);
    setError('');
    onConfirm(mapFormValuesToPreference(form), selectedPreferenceIndex);
  };

  const onChange = (name, value) => {
    if (name === FORM_FIELD_NAMES.sector) {
      setSelectedSectorId(value);
      form.setValue(FORM_FIELD_NAMES.subsector, '');
    }
    if (name === FORM_FIELD_NAMES.category) {
      form.reset();
    }
    form.setValue(name, value);
  };

  const saveButtonTooltip = (): string => {
    return SAVE_BUTTON_TOOLTIPS[formValues.category];
  };

  const renderFooter = () => (
    <>
      <Button
        dataTestId="add-preferences-cancel-button"
        disabled={isSaving}
        mode={ButtonMode.SECONDARY}
        onClick={closeModal}
      >
        Cancel
      </Button>
      <Tooltip
        content={saveButtonTooltip()}
        disabled={!saveDisabled || isSaving}
        trigger={
          <div>
            <Button
              dataTestId="add-preferences-confirm-button"
              type="submit"
              onClick={form.handleSubmit(submitForm)}
              disabled={saveDisabled || !isDirty}
              loading={isSaving}
              mode={ButtonMode.PRIMARY}
            >
              {selectedPreference ? 'Save Changes' : 'Save'}
            </Button>
          </div>
        }
      />
    </>
  );

  return (
    <Modal
      headerTitle={selectedPreference ? FORM_HEADER_TITLES.editPreferences : FORM_HEADER_TITLES.addPreferences}
      onClose={closeModal}
      open
      footer={renderFooter()}
      width={ModalWidth.Medium}
      closeOnEscape={isModalCloseable}
      closeOnDimmerClick={isModalCloseable}
      error={formError}
      dataTestId="add-preference-modal"
      isScrollable={false}
      isLoading={isLoading}
      closeOnButtonClick={isModalCloseable}
    >
      <FormProvider {...form}>
        <AddPreferencesFormContainer
          onChange={onChange}
          dropdownOptions={{
            ...addPreferenceModalOptions,
            subsector: selectedSectorId ? subsectorOptionsBySector[selectedSectorId] : [],
          }}
          selectedPreference={selectedPreference}
        />
      </FormProvider>
    </Modal>
  );
};

export default AddPreferencesModal;
